import {
  API_END_POINT,
  axiosInstance,
  ENTITY_MANAGER_END_POINTS,
  validateApiParams,
  WorkflowCaseNamePayloadType,
} from '@app-store'
import { FieldValues } from 'react-hook-form'
import { STRINGS } from '../utils/strings'
import { NAMESPACES } from '../utils/constants'
import { DropdownType } from '@ui-kit'
import { enhanceWithState } from '../utils/form.utils'

export const addEntityData = async (
  entityName: string,
  entityId: number,
  layoutId: number,
  data: FieldValues,
  _embedded: string[] = []
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}`
  validateApiParams(
    { entityName },
    {
      endpoint,
      method: 'POST',
    }
  )

  const formattedData = {
    ...enhanceWithState(data),
    _state: NAMESPACES.ADDED,
  }
  return await axiosInstance.post(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: formattedData,
    },
    { params: { entityId, layoutId } }
  )
}

export const mapWebEnrollmentWithCase = async (
  caseId: string,
  dataId: string
) => {
  const endpoint = `${API_END_POINT.CORE}hcp/microsite/econfig/map/${dataId}/case/${caseId}`
  validateApiParams(
    { caseId, dataId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}
export const editEntityData = async (
  entityName: string,
  entityId: number,
  layoutId: number,
  data: FieldValues,
  dataId: number | string,
  _embedded: string[] = []
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${dataId}`
  validateApiParams(
    { entityName, dataId },
    {
      endpoint,
      method: 'PUT',
    }
  )

  const enhanceWithState = (obj: FieldValues): FieldValues => {
    if (Array.isArray(obj)) {
      return obj.map((item) => enhanceWithState(item) as FieldValues)
    }

    if (obj && typeof obj === 'object') {
      if ('isOverriddenSubForm' in obj) {
        return {}
      }

      return {
        ...Object.fromEntries(
          Object.entries(obj).map(([key, val]) => [key, enhanceWithState(val)])
        ),
        _state: obj._state || NAMESPACES.CHANGED,
      }
    }

    return obj
  }

  const formattedData = {
    ...enhanceWithState(data),
    _state: NAMESPACES.CHANGED,
  }
  return await axiosInstance.put(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: formattedData,
    },
    { params: { entityId, layoutId } }
  )
}

export const editEntityDataEmbeddedAPI = async (
  entityName: string,
  dataId: number | string,
  data: FieldValues,
  embedded: string[] = [],
  isEntityId?: boolean
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${dataId}`
  validateApiParams(
    { entityName, dataId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      [isEntityId ? 'entity_id' : 'entity_name']: entityName,
      embedded: embedded,
    },
    data: data,
  })
}

export const getEntityData = async (
  entityName: string,
  entityId: number,
  layoutId: number,
  id: number | string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${id}`
  validateApiParams(
    { entityName, id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint, {
    params: {
      layoutId,
      entityId,
    },
  })
}
export const getEntityDataByEntityNameAndLayout = async (
  entityName: string,
  layoutId: number,
  id: number | string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${id}`
  validateApiParams(
    { entityName, id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint, {
    params: {
      layoutId,
      entityName,
    },
  })
}

export const getEntityDataByIdEmbedded = async (
  entityName: string,
  entityId: number,
  columns: string[],
  id: number | string
) => {
  validateApiParams(
    { entityName, entityId, id },
    {
      endpoint: `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${id}`,
      method: 'GET',
    }
  )
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${id}`,
    {
      params: {
        embedded: [...columns].join(','),
        entityId,
      },
    }
  )
}

export const getCaseKeyCaseNameByEntity = async (
  payload: WorkflowCaseNamePayloadType,
  entityName: string,
  id: number | string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/get/${entityName}/${id}`
  validateApiParams(
    { entityName, id },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    ...payload,
  })
}

export const getEntityDataByIdEntityNameEmbedded = async (
  entityName: string,
  columns: string[],
  id?: number | string
) => {
  if (id) {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}/${id}`
    validateApiParams(
      { entityName, id },
      {
        endpoint,
        method: 'GET',
      }
    )
    return await axiosInstance.get(endpoint, {
      params: {
        embedded: [...columns].join(','),
        entityName,
      },
    })
  } else {
    const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${entityName}`
    validateApiParams(
      { entityName },
      {
        endpoint,
        method: 'GET',
      }
    )
    return await axiosInstance.get(endpoint, {
      params: {
        embedded: [...columns].join(','),
        entityName,
      },
    })
  }
}

export const getEntityDataEmbedded = async (
  entityName: string | null,
  entityId: number,
  columns: string[]
) => {
  const identifier = entityName ?? entityId
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${identifier}/paginated`
  validateApiParams(
    { identifier },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint, {
    params: {
      embedded: [...columns].join(','),
      size: 10000,
      entityId,
    },
  })
}

export const postValidateCasesWithAgent = async (
  agentId: string,
  caseIds: string[]
) => {
  const endpoint = `${API_END_POINT.CORE}case_assignment/validate?caseIds=${caseIds}&userId=${agentId}`
  validateApiParams(
    { agentId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const assignCasesWithAgent = async (
  rowIds: string[],
  agent: DropdownType<string> | null,
  layoutId: string | undefined,
  entityId: string | undefined,
  entityName: string
) => {
  if (entityName === 'case') {
    return await assignUnassignTasksWithCases(rowIds, agent)
  }
  const data = {
    users_id: agent?.id,
    assigned_to: agent?.label,
  }

  return await axiosInstance.patch(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/entities?ids=${rowIds}`,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data,
    }
  )
}

export const assignUnassignTasksWithCases = async (
  rowIds: string[],
  agent: DropdownType<string> | null,
  isUnassign = false
) => {
  return await axiosInstance.post(
    `${API_END_POINT.WORKFLOW}/${ENTITY_MANAGER_END_POINTS.CASE_MANAGEMENT}/assign?unassign=${isUnassign}`,
    {
      case_ids: rowIds,
      user_id: agent?.id,
    }
  )
}

export const unassignCasesWithAgent = async (
  rowIds: string[],
  agent: DropdownType<string> | null,
  layoutId: string | undefined,
  entityId: string | undefined,
  entityName: string | undefined
) => {
  if (entityName === 'case' || entityName === 'hcp_portal_entity_cases') {
    return await assignUnassignTasksWithCases(rowIds, agent, true)
  }
  return await axiosInstance.patch(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/entities?ids=${rowIds}`,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: {
        users: {
          id: agent?.id,
          _state: STRINGS.REMOVED,
        },
        assigned_to: agent?.label,
      },
    }
  )
}

export const getEntityDataByPhoneNumber = async (phoneNumber: string) => {
  const endpoint = `${API_END_POINT.CTI}?phoneNumber=${phoneNumber}`
  validateApiParams(
    { phoneNumber },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const executeCustomProcedure = async (
  procedureName: string,
  case_id: string,
  eligibility_assessment_id: string
) => {
  return await axiosInstance.post(
    `${API_END_POINT.V3_ENTITY_MANAGER}entity-data/custom/procedure`,
    {
      procedureName: procedureName,
      params: {
        case_id: {
          order: 1,
          value: case_id,
          is_input_param: true,
          type: 'LONGDATATYPE',
        },
        c_eligibility_assessment_id: {
          order: 2,
          value: eligibility_assessment_id,
          is_input_param: false,
          type: 'LONGDATATYPE',
        },
      },
    }
  )
}

export const reportTemplateUsage = async (id: string) => {
  const endpoint = `${API_END_POINT.TEMPLATE}/v1/document-audit/${id}/activate`
  validateApiParams(
    { id },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint)
}

export const assignUnassignForDashBoard = async (
  rowIds: string,
  entityId: number,
  layoutId: number,
  agentId: number | null,
  entity_name: string,
  userId: string
) => {
  return axiosInstance.patch(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/${entity_name}`,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: {
        [userId]: agentId,
      },
    },
    {
      params: {
        ids: rowIds,
      },
    }
  )
}
